import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { UtilService, NotificationService } from '../../../shared/index';
import { EventsV4Service } from '../../shared/events-v4.service';
import { EventV4SponsorsService } from '../../shared/sponsors/event-v4-sponsors.service';
import { EventSponsor } from '../../shared/sponsors/event-sponsor.model';
import { ProfileRead } from '../../../profiles/shared/profile-read.model';
import { AsyncState } from '../../../shared/model/index';
import { UserService } from '../../../users/shared/user.service';
import { TranslationService } from '../../../shared/translation/translation.service';
import { moveItemInArray } from '@angular/cdk/drag-drop';
import { NavigationService } from '../../../shared/navigation/navigation.service';

@Component({
  selector: 'wl-event-v4-settings-sponsors',
  styleUrls: ['event-v4-settings-sponsors.component.css'],
  templateUrl: 'event-v4-settings-sponsors.component.html',
  providers: [EventsV4Service, EventV4SponsorsService, UserService],
  standalone: false,
})
export class EventV4SettingsSponsorsComponent implements OnInit {
  public _sponsorIdToDelete: number = 0;
  public loadingSponsor: AsyncState = new AsyncState();
  public sponsors: Array<EventSponsor>;
  public eventId: number;
  public loadingSponsors: boolean = false;
  public loadingSponsorsError: string = '';
  public saving: boolean = false;
  public savingError: string = '';
  public deleting: boolean = false;
  public deleteError: string = '';
  public selectedSponsor: EventSponsor;
  public userAccessLevel: number;
  public showEditModal = false;
  public showDeleteModal = false;

  public constructor(
    private _route: ActivatedRoute,
    private _utilService: UtilService,
    private _eventSponsorsService: EventV4SponsorsService,
    private _userService: UserService,
    private _notificationService: NotificationService,
    private _translationService: TranslationService,
    private _navigationService: NavigationService
  ) {
    this.sponsors = new Array<EventSponsor>();
    this.eventId = 0;
    this.selectedSponsor = new EventSponsor();
    this.userAccessLevel = 0;
  }

  public getSponsorLogoSrc(url: string): string {
    if (!url) {
      return url;
    }
    if (url.startsWith('/')) {
      return this._navigationService.getLegacyDomain() + url;
    }
    return url;
  }

  public loadEventSponsors() {
    this.loadingSponsors = true;
    this.loadingSponsorsError = '';

    this._eventSponsorsService
      .getEventEventSponsors(this.eventId)
      .then((data: any) => {
        this.loadingSponsors = false;
        this.sponsors = data.Result;
        if (this.sponsors.length > 0) {
          this.sponsors = this.sponsors.sort((a, b) => +a.Sort - +b.Sort);
          this.selectedSponsor = this.sponsors[0];
        }
      })
      .catch((error) => {
        this.loadingSponsors = false;
        this.loadingSponsorsError = this._utilService.extractErrorMessage(error);
      });
  }

  public newSponsor = (): void => {
    this.saving = false;
    this.savingError = '';

    this.loadingSponsor.busy = false;
    this.loadingSponsor.message = '';

    let newSponsor: EventSponsor = new EventSponsor();
    newSponsor.EventId = this.eventId;
    this.selectedSponsor = newSponsor;
    this.showEditModal = true;
  };

  public saveSponsors = (): void => {
    this.saving = true;
    this.savingError = '';

    this._eventSponsorsService
      .upsertEventSponsor(this.selectedSponsor)
      .then(() => {
        this.saving = false;
        this.loadEventSponsors();
        this.showEditModal = false;

        this._translationService
          .getTitlecase('events.settings.sponsors.save-success-message')
          .then((translation) => this._notificationService.success(translation));
      })
      .catch((error) => {
        this.saving = false;
        this.savingError = this._utilService.extractErrorMessage(error);
      });
  };

  public editSponsor = (sponserId: number): void => {
    this.saving = false;
    this.savingError = '';

    this.loadingSponsor.busy = true;
    this.loadingSponsor.message = '';
    this.loadingSponsor.success = false;

    this._eventSponsorsService
      .getEventEventSponsor(sponserId)
      .then((data) => {
        this.loadingSponsor.busy = false;
        this.loadingSponsor.success = true;
        this.selectedSponsor = data;
        this.showEditModal = true;
      })
      .catch((error) => {
        this.loadingSponsor.busy = false;
        this.loadingSponsor.message = this._utilService.extractErrorMessage(error);
        this.loadingSponsor.success = false;
      });
  };

  public confirmDeleteSponsor(sponsorId: number): void {
    this.deleting = false;
    this.deleteError = '';
    this._sponsorIdToDelete = sponsorId;
    this.showDeleteModal = true;
  }

  public deleteSponsor = (): void => {
    this.deleting = true;
    this.deleteError = '';

    this._eventSponsorsService
      .deleteEventSponsor(this._sponsorIdToDelete)
      .then(() => {
        this.deleting = false;
        this.showDeleteModal = false;
        this.loadEventSponsors();
      })
      .catch((error) => {
        this.deleting = false;
        this.deleteError = this._utilService.extractErrorMessage(error);
      });
  };
  public sponsorProfileChanged = (profile: ProfileRead): void => {
    if (this.selectedSponsor.SponserId <= 0 && profile && profile.ProfileId > 0) {
      this.selectedSponsor.SponsorName = profile.ReportName;

      if (profile.Website)
        this.selectedSponsor.SponsorWebsite = /^https?:\/\//i.test(profile.Website.trim()) ? profile.Website : 'http://' + profile.Website;
    }
  };

  public sorted(event) {
    moveItemInArray(this.sponsors, event.previousIndex, event.currentIndex);
    this._eventSponsorsService.reorderEventSponsors(this.eventId, this.sponsors);
  }

  ngOnInit() {
    this._route.params.subscribe((params) => {
      if (this._utilService.isNumeric(params['id'])) {
        let id = Number(params['id']);
        this.eventId = id;

        this.loadEventSponsors();
      }
    });

    this._userService.getCurrentUserSecurityElementAccessLevel('Events_Module').then((data: number) => {
      this.userAccessLevel = data;
    });
  }
}
